import { useState, useEffect, useCallback } from 'react';
import cloudflareAPI from '../../services/api/cloudflareAPI';
import type { MarketData } from '../../services/api/cloudflareAPI';
import type { Instrument, SahamInstrument } from '../../types/ai';
// In-memory cache removed — Service Worker handles network caching

// Stock market data interface
export interface StockMarketData {
  instrument: SahamInstrument;
  category: 'saham';
  name: string;
  currency: 'IDR';
  open: number;
  high: number;
  low: number;
  close: number;
  previousClose: number;
  change: number;
  changePercent: number;
  volume: number;
  volumeDescription?: string;
  timestamp: Date;
  yahooSymbol: string;
  marketStatus: 'OPEN' | 'CLOSED' | 'PRE_MARKET' | 'POST_MARKET';
}

export interface IDXTradingSession {
  isOpen: boolean;
  currentTime: string;
  openTime: string;
  closeTime: string;
  timezone: 'Asia/Jakarta';
  sessionPhase: 'PRE_OPENING' | 'OPENING' | 'REGULAR' | 'PRE_CLOSING' | 'CLOSING' | 'CLOSED';
}

interface UseMarketDataResult {
  marketData: MarketData[];
  loading: boolean;
  error: string | null;
  refreshData: () => Promise<void>;
  lastUpdate: Date | null;
  dataSource: 'backend';
}

interface UseInstrumentDataResult {
  data: MarketData | null;
  loading: boolean;
  error: string | null;
  refreshData: () => Promise<void>;
  lastUpdate: Date | null;
}

export function useMarketData(): UseMarketDataResult {
  const [marketData, setMarketData] = useState<MarketData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [dataSource, setDataSource] = useState<'backend'>('backend');

  const isDataStale = (data: MarketData[]): boolean => {
    if (data.length === 0) {
      console.warn('Market data is empty, considering stale');
      return true;
    }

    // Check timestamp - if older than 4 hours, consider stale
    const lastTimestamp = new Date(data[0].timestamp);
    const fourHoursAgo = new Date(Date.now() - 4 * 60 * 60 * 1000);
    if (lastTimestamp < fourHoursAgo) {
      console.warn(`Market data is older than 4 hours (${lastTimestamp.toISOString()}), considering stale`);
      return true;
    }

    return false;
  };

  const fetchMarketData = useCallback(async () => {
    try {
      setError(null);
      
      // Fetch from backend (Service Worker caches the network response)
      const data = await cloudflareAPI.getMarketData();

      // Check if backend data seems stale
      if (isDataStale(data)) {
        console.warn('Backend data appears stale');
      }

      setMarketData(data);
      setDataSource('backend');
      setLastUpdate(new Date());
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch market data';
      setError(errorMessage);
      console.error('Market data fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshData = useCallback(async () => {
    setLoading(true);
    await fetchMarketData();
  }, [fetchMarketData]);

  useEffect(() => {
    // Initial fetch only
    void fetchMarketData();
  }, [fetchMarketData]); // Include fetchMarketData dependency

  return {
    marketData,
    loading,
    error,
    refreshData,
    lastUpdate,
    dataSource
  };
}

export function useInstrumentData(instrument: Instrument): UseInstrumentDataResult {
  const [data, setData] = useState<MarketData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  const fetchInstrumentData = useCallback(async () => {
    try {
      setError(null);
      const instrumentData = await cloudflareAPI.getInstrumentData(instrument);
      setData(instrumentData);
      setLastUpdate(new Date());
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch instrument data';
      setError(errorMessage);
      console.error('Instrument data fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [instrument]);

  const refreshData = useCallback(async () => {
    setLoading(true);
    await fetchInstrumentData();
  }, [fetchInstrumentData]);

  useEffect(() => {
    // Initial fetch only
    void fetchInstrumentData();
  }, [instrument, fetchInstrumentData]); // Re-fetch when instrument changes or fetchInstrumentData changes

  return {
    data,
    loading,
    error,
    refreshData,
    lastUpdate
  };
}

// ============================================
// STOCK (SAHAM) DATA HOOKS
// ============================================

interface UseStockDataResult {
  stockData: StockMarketData[];
  loading: boolean;
  error: string | null;
  refreshData: () => Promise<void>;
  lastUpdate: Date | null;
  idxSession: IDXTradingSession | null;
}

interface UseStockInstrumentDataResult {
  data: StockMarketData | null;
  loading: boolean;
  error: string | null;
  refreshData: () => Promise<void>;
  lastUpdate: Date | null;
}

/**
 * Hook to fetch all stock data from backend
 */
export function useStockData(): UseStockDataResult {
  const [stockData, setStockData] = useState<StockMarketData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [idxSession, setIdxSession] = useState<IDXTradingSession | null>(null);

  const fetchStockData = useCallback(async () => {
    try {
      setError(null);

      // Fetch from backend (Service Worker caches the network response)
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/market/stocks`);
      if (!response.ok) {
        throw new Error('Failed to fetch stock data');
      }

      const result = await response.json();
      if (result.success && result.data) {
        setStockData(result.data);
        setIdxSession(result.session);
        setLastUpdate(new Date());
      } else {
        throw new Error(result.error || 'Failed to fetch stock data');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch stock data';
      setError(errorMessage);
      console.error('Stock data fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshData = useCallback(async () => {
    setLoading(true);
    await fetchStockData();
  }, [fetchStockData]);

  useEffect(() => {
    void fetchStockData();
  }, [fetchStockData]);

  return {
    stockData,
    loading,
    error,
    refreshData,
    lastUpdate,
    idxSession
  };
}

/**
 * Hook to fetch single stock instrument data
 */
export function useStockInstrumentData(instrument: SahamInstrument): UseStockInstrumentDataResult {
  const [data, setData] = useState<StockMarketData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  const fetchStockInstrumentData = useCallback(async () => {
    try {
      setError(null);

      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/market/stocks/${instrument}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch stock data for ${instrument}`);
      }

      const result = await response.json();
      if (result.success && result.data) {
        setData(result.data);
        setLastUpdate(new Date());
      } else {
        throw new Error(result.error || `Failed to fetch stock data for ${instrument}`);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch stock data';
      setError(errorMessage);
      console.error('Stock instrument data fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [instrument]);

  const refreshData = useCallback(async () => {
    setLoading(true);
    await fetchStockInstrumentData();
  }, [fetchStockInstrumentData]);

  useEffect(() => {
    void fetchStockInstrumentData();
  }, [instrument, fetchStockInstrumentData]);

  return {
    data,
    loading,
    error,
    refreshData,
    lastUpdate
  };
}

/**
 * Hook to fetch all market data (stocks)
 */
export function useAllMarketData(): {
  marketData: MarketData[];
  stockData: StockMarketData[];
  loading: boolean;
  error: string | null;
  refreshData: () => Promise<void>;
  lastUpdate: Date | null;
} {
  const { marketData, loading: marketLoading, error: marketError, refreshData: refreshMarket, lastUpdate: marketUpdate } = useMarketData();
  const { stockData, loading: stockLoading, error: stockError, refreshData: refreshStock, lastUpdate: stockUpdate } = useStockData();

  const refreshData = useCallback(async () => {
    await Promise.all([refreshMarket(), refreshStock()]);
  }, [refreshMarket, refreshStock]);

  return {
    marketData,
    stockData,
    loading: marketLoading || stockLoading,
    error: marketError || stockError,
    refreshData,
    lastUpdate: marketUpdate || stockUpdate
  };
}